import { oc } from "@orpc/contract";
import * as z from "zod";

/** The provider id every company model runs under; model ids carry the provider slug. */
export const DROK_COMPANY_MODEL_PROVIDER_ID = "drok-company";

export const DROK_COMPANY_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MAX_MODELS = 200;
const MAX_HEADERS = 20;

/** `env` is the legacy DROK_MODELS provider; `drok-company` is the pi provider id itself. */
export const DROK_COMPANY_RESERVED_SLUGS: readonly string[] = [
  "env",
  DROK_COMPANY_MODEL_PROVIDER_ID,
];

export const CompanyModelSlugSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(DROK_COMPANY_SLUG_PATTERN, "Use lowercase letters, digits and single hyphens")
  .refine((slug) => !DROK_COMPANY_RESERVED_SLUGS.includes(slug), "This slug is reserved");

/** http(s) only, no userinfo, no query or fragment. A trailing slash is dropped so the stored value is canonical. */
export const CompanyModelBaseUrlSchema = z
  .string()
  .trim()
  .min(1)
  .max(2048)
  .refine((raw) => {
    try {
      const url = new URL(raw);
      return (
        (url.protocol === "http:" || url.protocol === "https:") &&
        !url.username &&
        !url.password &&
        !url.search &&
        !url.hash &&
        !raw.includes("?") &&
        !raw.includes("#")
      );
    } catch {
      return false;
    }
  }, "Must be an absolute http or https URL without credentials, query or fragment")
  .transform((raw) => raw.replace(/\/+$/, ""));

export const CompanyModelApiKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(2048)
  .regex(/^[\x21-\x7e]+$/, "Must contain only visible ASCII characters");

const HEADER_NAME = /^[A-Za-z0-9-]{1,100}$/;
/** Hop-by-hop and framing headers, plus `authorization`: the API key field is the auth. */
const DENIED_HEADERS = new Set([
  "host",
  "content-length",
  "transfer-encoding",
  "connection",
  "upgrade",
  "cookie",
  "authorization",
]);
export function isDeniedCompanyHeader(name: string): boolean {
  const lower = name.toLowerCase();
  return DENIED_HEADERS.has(lower) || lower.startsWith("proxy-");
}
export const CompanyModelHeadersSchema = z
  .record(
    z
      .string()
      .regex(HEADER_NAME, "Invalid header name")
      .refine((name) => !isDeniedCompanyHeader(name), "This header is not allowed"),
    z
      .string()
      .max(2048)
      .regex(/^[\x20-\x7e]*$/, "Invalid header value"),
  )
  .refine((headers) => Object.keys(headers).length <= MAX_HEADERS, "Too many headers");

const positiveInt = z.number().int().positive().max(100_000_000);

export const CompanyModelSchema = z
  .object({
    id: z
      .string()
      .min(1)
      .max(200)
      .regex(/^[^\s\p{Cc}]+$/u, "No whitespace or control characters"),
    name: z.string().trim().min(1).max(200).optional(),
    contextWindow: positiveInt,
    maxTokens: positiveInt,
    vision: z.boolean().optional(),
    reasoning: z.boolean().optional(),
  })
  .refine((model) => model.maxTokens < model.contextWindow, {
    message: "Max tokens must be smaller than the context window",
    path: ["maxTokens"],
  });
export type CompanyModelInput = z.infer<typeof CompanyModelSchema>;

export const CompanyModelListSchema = z
  .array(CompanyModelSchema)
  .max(MAX_MODELS)
  .refine((models) => new Set(models.map((model) => model.id)).size === models.length, {
    message: "Model ids must be unique",
  });

const NameSchema = z.string().trim().min(1).max(100);

export const CompanyProviderCreateSchema = z.object({
  name: NameSchema,
  slug: CompanyModelSlugSchema,
  baseUrl: CompanyModelBaseUrlSchema,
  apiKey: CompanyModelApiKeySchema,
  headers: CompanyModelHeadersSchema.optional(),
  enabled: z.boolean().default(true),
  models: CompanyModelListSchema,
});
export type CompanyProviderCreate = z.infer<typeof CompanyProviderCreateSchema>;

/** The key is write-only: omit it (or send blank) to keep the stored one. The slug cannot change. */
export const CompanyProviderUpdateSchema = z.object({
  id: z.string().min(1).max(200),
  name: NameSchema,
  baseUrl: CompanyModelBaseUrlSchema,
  apiKey: z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    CompanyModelApiKeySchema.optional(),
  ),
  /** Omitted keeps the stored headers; an object (even empty) replaces them. */
  headers: CompanyModelHeadersSchema.optional(),
  enabled: z.boolean(),
  models: CompanyModelListSchema,
});
export type CompanyProviderUpdate = z.infer<typeof CompanyProviderUpdateSchema>;

export const CompanyProviderViewSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  /** True when the stored secret cannot be read; IT can remove the row or save it again. */
  unreadable: z.boolean().optional(),
  /** Host only (with port); never the path, query or credentials. */
  baseUrlHost: z.string(),
  /** The full base URL, so the edit form can show it. IT-only; contains no secret. */
  baseUrl: z.string(),
  apiKeySet: z.literal(true),
  headerNames: z.array(z.string()),
  models: z.array(
    z.object({
      id: z.string(),
      name: z.string().optional(),
      contextWindow: z.number(),
      maxTokens: z.number(),
      vision: z.boolean().optional(),
      reasoning: z.boolean().optional(),
    }),
  ),
  updatedAt: z.string(),
});
export type CompanyProviderView = z.infer<typeof CompanyProviderViewSchema>;

export const CompanyModelsListOutputSchema = z.object({
  providers: z.array(CompanyProviderViewSchema),
  /** True when DROK_MODELS_* env still defines a legacy provider (shown read-only). */
  envProvider: z.object({ baseUrlHost: z.string(), modelIds: z.array(z.string()) }).nullable(),
});

const OkSchema = z.object({ ok: z.literal(true) });

/** IT-only: not in the employee access map, so default-deny. */
export const drokCompanyModelsContract = {
  list: oc.output(CompanyModelsListOutputSchema),
  create: oc.input(CompanyProviderCreateSchema).output(CompanyProviderViewSchema),
  update: oc.input(CompanyProviderUpdateSchema).output(CompanyProviderViewSchema),
  remove: oc.input(z.object({ id: z.string().min(1).max(200) })).output(OkSchema),
  probe: oc
    .input(
      z
        .object({
          baseUrl: CompanyModelBaseUrlSchema,
          apiKey: CompanyModelApiKeySchema.optional(),
          providerId: z.string().min(1).max(200).optional(),
          headers: CompanyModelHeadersSchema.optional(),
        })
        .refine((input) => input.apiKey || input.providerId, {
          message: "Enter an API key or pick a saved provider",
        }),
    )
    .output(z.object({ models: z.array(z.string()) })),
};
